import { useMemo } from 'react';

import type { SessionData, EnrollBannerData, OtherSession, CountdownData } from 'src/types/classSession';
import { useGetLearningObjectsById } from 'src/api/hooks/useGetLearningObjectsById';
import { ContentfulInstructorLedTraining } from 'src/graphql/queries/GetInstructorLedTraining';

import {
  extractOtherSessions,
  extractSessionDateInfo,
  findEnrollmentForInstance,
  findResourceForInstance,
  isEnrolledInInstance,
} from '../utils/sessionResourceHelpers';
import { formatSessionDate } from '../utils/formatSessionDate';
import { calculateCountdown } from '../utils/calculateCountdown';

interface UseSessionDataResult {
  isLoading: boolean;
  error: Error | null;
  isEnrolled: boolean;
  enrollmentId?: string;
  tags: string[];
  session: SessionData;
  otherSessions: OtherSession[];
  enrollBanner: EnrollBannerData;
}

const EMPTY_COUNTDOWN: CountdownData = {
  days: 0,
  hours: 0,
  minutes: 0,
  isLessThanThirtyMinutes: false,
};

const extractStudentMaterialsUrl = (iltData?: ContentfulInstructorLedTraining): string => {
  const material = iltData?.studentMaterialsCollection?.items?.[0];

  return material?.__typename === 'Document' ? material.studentUrl ?? '' : '';
};

const createEmptySession = (instanceId: string): SessionData => ({
  id: instanceId,
  dateStart: null,
  dateEnd: null,
  duration: 0,
  instructorNames: [],
  location: null,
  ...formatSessionDate(null, null, 0),
});

const createEmptyBannerData = (materialsLink: string): EnrollBannerData => ({
  meetingUrl: '',
  materialsLink,
  countdown: EMPTY_COUNTDOWN,
  courseName: '',
  isCompleted: false,
  completionDate: null,
});

export const useSessionData = (
  courseId: string,
  instanceId: string,
  contentfulILTData?: ContentfulInstructorLedTraining,
): UseSessionDataResult => {
  const { data: learningObjectResponse, isLoading, error } = useGetLearningObjectsById(courseId);

  return useMemo(() => {
    const materialsLink = extractStudentMaterialsUrl(contentfulILTData);
    const emptySession = createEmptySession(instanceId);
    const courseData = learningObjectResponse?.data?.data?.[0];

    if (isLoading || error || !courseData) {
      return {
        isLoading,
        error: error as Error | null,
        isEnrolled: false,
        enrollmentId: undefined,
        tags: [],
        session: emptySession,
        otherSessions: [],
        enrollBanner: createEmptyBannerData(materialsLink),
      };
    }

    const includedResources = learningObjectResponse.data.included || [];
    const sessionResource = findResourceForInstance(includedResources, instanceId);
    const enrollment = findEnrollmentForInstance(includedResources, instanceId);

    const session = sessionResource?.attributes
      ? buildSessionFromResource(instanceId, sessionResource.attributes)
      : emptySession;

    const courseName = courseData.attributes?.localizedMetadata?.[0]?.name || '';
    const isCompleted = enrollment?.attributes?.state === 'COMPLETED';
    const completionDate = enrollment?.attributes?.dateCompleted || null;

    return {
      isLoading: false,
      error: null,
      isEnrolled: isEnrolledInInstance(courseData, instanceId),
      enrollmentId: courseData.relationships?.enrollment?.data?.id,
      tags: courseData.attributes?.tags || [],
      session,
      otherSessions: extractOtherSessions(includedResources, instanceId, courseId),
      enrollBanner: {
        meetingUrl: session.location || '',
        materialsLink,
        countdown: calculateCountdown(session.dateStart),
        courseName,
        isCompleted,
        completionDate,
      },
    };
  }, [learningObjectResponse, isLoading, error, instanceId, courseId, contentfulILTData]);
};

const buildSessionFromResource = (
  instanceId: string,
  attributes: { instructorNames?: string[]; location?: string },
): SessionData => {
  const { dateStart, dateEnd, duration, month, day, label, time } = extractSessionDateInfo(attributes);

  return {
    id: instanceId,
    dateStart,
    dateEnd,
    duration,
    instructorNames: attributes.instructorNames || [],
    location: attributes.location || null,
    month,
    day,
    label,
    time,
  };
};
